import { getInstance } from '../api/service'

export const DEVICE_UPDATE_INTERVAL_MS = 60000
export const THROTTLE_INTERVAL = 5000

export const getTelemetryAverages = async (deviceId, deviceType) => {
  let averages
  const api = getInstance()

  try {
    const resp = await api.getTelemetryAverages(deviceId, deviceType)
    if (resp.ok) {
      const body = await resp.json()
      if (body) {
        averages = body
      }
    }
  } catch (error) {
    // ignore, returns undefined averages
  }

  return averages
}

export const getPercentChanged = async (deviceId) => {
  let percentChanged
  const api = getInstance()

  try {
    const pctResp = await api.getTelemetryPercentChanged(deviceId)
    if (pctResp.ok) {
      const body = await pctResp.json()
      if (body) {
        percentChanged = body
      }
    }
  } catch (error) {
    // ignore, returns undefined percentChanged
  }

  return percentChanged
}

export const getLatestTelemetry = async (deviceId) => {
  let telemetry = {}
  const api = getInstance()
  try {
    const resp = await api.getTelemetryLatest(deviceId)
    if (resp.ok) {
      const body = await resp.json()
      if (body) {
        telemetry.readings = body
        telemetry.timestamp = body.timestamp
        telemetry.lastTelemetryStudyId = body.study_id
      }
    }
  } catch (error) {
    // ignore, returns empty telemetry
  }

  return telemetry
}

export const getLatestStudy = async (deviceId, includeChildren, includeOnlyLatestTelemetry) => {
  let study = null
  const api = getInstance()
  try {
    const resp = await api.getLatestDeviceStudy(deviceId, includeChildren, includeOnlyLatestTelemetry)
    if (resp.ok) {
      study = await resp.json()
    }
  } catch (error) {
    // ignore, return empty study
  }

  return study
}

export const getDeviceDetails = async (deviceId) => {
  let detail = {}
  const api = getInstance()

  try {
    const resp = await api.getDevice(deviceId)
    if (resp.ok) {
      const body = await resp.json()

      detail.model = body.model
      detail.serial = body.serial

      if (body.cloud_state_data) {
        detail.name = body.cloud_state_data.name
        detail.locationName = body.cloud_state_data.location_name
        detail.indoor = body.cloud_state_data.isIndoor
        detail.isPublic = body.cloud_state_data.isPublic
        detail.zeroAppliedAt = body.cloud_state_data.zero_applied_at
        detail.zeroState = body.cloud_state_data.zero_state
      }

      if (body.device_state_data) {
        detail.timestamp = body.device_state_data.config_timestamp
        detail.firmware = body.device_state_data.firmware_version
        detail.rssi = body.device_state_data.ap_rssi
        detail.spsEoL = body.device_state_data.spsEoL
        detail.connectionType = body.device_state_data.connection_type
        detail.calibrationDate = body.device_state_data.calibration_date
        detail.flowCalibrationFactor = body.device_state_data.flow_calibration_factor
        detail.userCal = body.device_state_data.user_cal
        detail.errorState = body.device_state_data.error_state
        detail.telemTxType = body.device_state_data.telem_tx_type
        detail.batteryCharging = body.device_state_data.battery_charging
        detail.batteryInstalled = body.device_state_data.battery_installed
        detail.batteryLow = body.device_state_data.battery_low
        detail.batteryRemaining = body.device_state_data.battery_remaining
        detail.uploadStatus = body.device_state_data.upload_status
        detail.isConnected = body.device_state_data.connected
        detail.alarms = body.device_state_data.alarms
      }
    }
  } catch (error) {
    // ignore, returns empty detail
  }

  return detail
}

// streamLatestTelemetry makes a call to the devices API to open a SSE stream of latest telemetry
// the handler function is defined by the caller to process each reading
// the startStream helper function gets the stream ReadableStreamDefaultReader object and calls
// pump to begin calling the handler in the background
// adding a catch to stream.closed allows us to automatically restart the stream if the server times out
export const streamLatestTelemetry = async (deviceId, handler) => {
  // create an object so the attributes can be modified by reference in callers
  let returnObject = {}
  const api = getInstance()

  const startStream = async () => {
    let stream
    try {
      const response = await api.streamTelemetryLatest(deviceId)
      if (!response.ok) {
        throw new Error('Failed to fetch telemetry stream')
      }
      stream = response.body.getReader()
      // set up stream to automatically restart if it is closed
      stream?.closed.catch(async () => {
        await startStream()
      })
      returnObject.stream = stream
    } catch (error) {
      return // This will break out of the startStream function
    }

    pump(stream, handler)
    return stream
  }

  await startStream()

  return returnObject
}

// Helper to process the ReadableStream and call a handler for each chunk
export const pump = async function (reader, handler, buffer = '') {
  const textDecoder = new TextDecoder()

  if (typeof handler !== 'function') {
    return
  }

  reader.read().then(({ done, value }) => {
    if (done) {
      return
    }

    // Append the decoded chunk to the buffer
    buffer += textDecoder.decode(value, { stream: true })

    // Split the buffer into messages using the delimiter
    const splitMessages = buffer.split('\n\n')
    buffer = splitMessages.pop() // Keep the last (possibly incomplete) part in the buffer

    // Process each complete message
    for (const message of splitMessages) {
      const trimmedEventData = message.replace(/^data: /, '')
      const data = JSON.parse(trimmedEventData)
      handler(data)
    }

    // get next value
    pump(reader, handler, buffer)
  }).catch(() => {
    // we can swallow this error. It likely occurs after stream is closed
    // which we handle in streamLatestTelemetry
  })
}
